import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  TableHead,
  Button,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const apiUrlGrupo = process.env.REACT_APP_API_GRUPO;
const apiUrlReservaQloApp = process.env.REACT_APP_API_RESERVAS_QLOAPP;
const apiUrlReservaID = process.env.REACT_APP_API_RESERVAS_ID;
const apiUrlHabitaciones = process.env.REACT_APP_API_HABITACIONES;

function DetalleReservaModal({ isOpen, onClose, reserva: initialReserva }) {
  const [reserva, setReserva] = useState(null);
  const [usuarioCreadorNombre, setUsuarioCreadorNombre] = useState('');
  const [habitaciones, setHabitaciones] = useState([]);
  const [asignaciones, setAsignaciones] = useState({});
  const [reservaQloApp, setReservaQloApp] = useState('');

  const navigate = useNavigate();

  // Función para redirigir a la vista de edición de reserva
  const handleEditReserva = (id) => {
    navigate(`/editar_reserva/${id}`);
  };

  // Recuperar datos del usuario
  useEffect(() => {
    const fetchUsuarioCreador = async () => {
      if (!initialReserva || !initialReserva.id) return; // Si no hay reserva, no hacemos nada
  
      try {
        const response = await axios.get(`${apiUrlReservaID}${initialReserva.id}`);
        console.log(`📌 Datos de la reserva recibidos en el modal:`, response.data);
  
        if (response.data.usuarioCreador && response.data.usuarioCreador.nombre_usuario) {
          setUsuarioCreadorNombre(response.data.usuarioCreador.nombre_usuario);
        } else {
          setUsuarioCreadorNombre('Nombre no disponible');
        }
      } catch (error) {
        console.error('❌ Error al obtener el usuario creador:', error);
        setUsuarioCreadorNombre('Error al cargar el nombre');
      }
    };
  
    if (isOpen) {
      fetchUsuarioCreador();
    }
  }, [isOpen, initialReserva]);
  
  // Obtener habitaciones disponibles
  useEffect(() => {
    const fetchHabitaciones = async () => {
      try {
        const response = await axios.get(apiUrlHabitaciones);
        const habitacionesProcesadas = response.data.map((habitacion) => ({
          ...habitacion,
          descripcion: `Nº ${habitacion.numero} - Planta ${habitacion.piso} - ${habitacion.cantidad_camas} cama(s)`,
        }))
        .sort((a, b) => a.numero - b.numero);
        setHabitaciones(habitacionesProcesadas);
      } catch (error) {
        console.error('Error al obtener habitaciones:', error);
      }
    };
    fetchHabitaciones();
  }, []);

  // Actualizar reservaQloApp cuando cambia la reserva
  useEffect(() => {
    if (isOpen && reserva && reserva.id) { 
      setReservaQloApp(reserva.reserva_qloapp ?? '');
    }
  }, [isOpen, reserva]); 

  // Función para obtener la reserva desde la API
  const fetchReserva = async (id, setReserva) => {
    if (!id) return;
    try {
      const response = await axios.get(`${apiUrlReservaID}${id}`);
      console.log("📌 API Response en React:", response.data);
      setReserva({ ...response.data });
    } catch (error) {
      console.error("❌ Error al obtener la reserva:", error);
    }
  };

  // Llamar a fetchReserva cuando el modal se abre
  useEffect(() => {
    if (isOpen && initialReserva?.id) {
      fetchReserva(initialReserva.id, setReserva);
    }
  }, [isOpen, initialReserva?.id]);

  // Actualizar N° de reserva en la API
  const handleActualizarReservaQloApp = async () => {
    try {
      console.log("📌 Enviando actualización a la API:", {
        reserva_id: reserva?.id,
        reserva_qloapp: reservaQloApp ? parseInt(reservaQloApp, 10) : null,
      });
      await axios.put(`${apiUrlReservaQloApp}/${reserva?.id}`, {
        reserva_id: reserva?.id,
        reserva_qloapp: reservaQloApp ? parseInt(reservaQloApp, 10) : null,
      });
      console.log("✅ Reserva actualizada correctamente");
      await fetchReserva(reserva?.id, setReserva);
      alert("Número de reserva actualizado correctamente.");
    } catch (error) {
      console.error("❌ Error al actualizar la reserva:", error.response?.data || error.message);
      alert("Error al actualizar la reserva.");
    }
  };

  // Asignar habitación a un miembro
  const handleAsignarHabitacion = async (miembroId, habitacionId) => {
    try {
      await axios.put(`${apiUrlGrupo}/asignar-habitacion`, {
        miembroId,
        habitacionId,
      });
      setAsignaciones((prev) => ({
        ...prev,
        [miembroId]: habitacionId,
      }));
      alert('Habitación asignada correctamente.');
    } catch (error) {
      console.error('Error al asignar habitación:', error);
      alert('Error al asignar la habitación.');
    }
  };

  if (!reserva) {
    return null;
  }
  const cantidadMasculinos = reserva?.miembros?.filter(m => m.genero === 'MASCULINO').length || 0;
  const cantidadFemeninos = reserva?.miembros?.filter(m => m.genero === 'FEMENINO').length || 0;


  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          maxHeight: '90%',
          overflow: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Detalle de la Reserva
        </Typography>
  
        {/* Botones de acción */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleEditReserva(reserva.id);
            }}
            sx={{ mr: 1 }}
          >
            Editar
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              // Aquí podrías llamar a handleOpenDeleteDialog si lo tienes implementado
            }}
          >
            Eliminar
          </Button>
        </Box>
  
        {/* Contenedor flex con tabla + resumen */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          {/* Información de la reserva */}
          <TableContainer component={Paper} sx={{ flex: 1, mr: 2 }}>
            <Table stickyHeader>
              <TableBody>
                <TableRow>
                  <TableCell><strong>ID de la reserva</strong></TableCell>
                  <TableCell>{reserva?.id || 'No disponible'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Usuario Creador</strong></TableCell>
                  <TableCell>{usuarioCreadorNombre || "Cargando..."}</TableCell> 
                </TableRow>
                <TableRow>
                  <TableCell><strong>Archivo</strong></TableCell>
                  <TableCell>
                    {reserva?.carpeta_aceptacion ? (
                      <Button
                        variant="outlined"
                        color="primary"
                        href={reserva.carpeta_aceptacion}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Ver archivo
                      </Button>
                    ) : (
                      'Sin archivo'
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Fecha de Entrada</strong></TableCell>
                  <TableCell>{reserva?.fecha_entrada || 'No disponible'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Fecha de Salida</strong></TableCell>
                  <TableCell>{reserva?.fecha_salida || 'No disponible'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>N° Reserva Asignada</strong></TableCell>
                  <TableCell>
                    <TextField
                      value={reserva?.miembros[0]?.reserva_qloapp ?? 'No disponible'}
                      size="small"
                      disabled
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><strong>Modificar N° Reserva</strong></TableCell>
                  <TableCell>
                    <TextField
                      value={reservaQloApp || ''}
                      onChange={(e) => setReservaQloApp(e.target.value)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleActualizarReservaQloApp}
                      disabled={!reserva?.id}
                    >
                      Guardar
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
  
          {/* Resumen por género */}
          <Paper elevation={3} sx={{ p: 3, minWidth: 220, backgroundColor: '#f9f9f9' }}>
            <Typography variant="subtitle1" gutterBottom><strong>Resumen por Género</strong></Typography>
            <Typography variant="body1">Masculinos: {cantidadMasculinos}</Typography>
            <Typography variant="body1">Femeninos: {cantidadFemeninos}</Typography>
          </Paper>
        </Box>
  
        {/* Listado de miembros */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          Miembros
        </Typography>
        <Box sx={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd' }}>
          <TableContainer component={Paper}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Nombre</strong></TableCell>
                  <TableCell><strong>Apellido</strong></TableCell>
                  <TableCell><strong>DNI</strong></TableCell>
                  <TableCell><strong>Edad</strong></TableCell>
                  <TableCell><strong>Genero</strong></TableCell>
                  <TableCell><strong>Federación/Deporte</strong></TableCell>
                  <TableCell><strong>Movilidad reducida</strong></TableCell>
                  <TableCell><strong>Rol</strong></TableCell>
                  <TableCell><strong>Alojado</strong></TableCell>
                  <TableCell><strong>Responsable</strong></TableCell>
                  <TableCell><strong>Calle</strong></TableCell>
                  <TableCell><strong>N° de calle</strong></TableCell>
                  <TableCell><strong>Código postal</strong></TableCell>
                  <TableCell><strong>Provincia</strong></TableCell>
                  <TableCell><strong>País</strong></TableCell>
                  <TableCell><strong>Asignar Habitación</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reserva?.miembros?.map((miembro, index) => (
                  <TableRow key={index}>
                    <TableCell>{miembro?.nombre || 'No disponible'}</TableCell>
                    <TableCell>{miembro?.apellido || 'No disponible'}</TableCell>
                    <TableCell>{miembro?.dni || 'No disponible'}</TableCell>
                    <TableCell>{miembro?.edad || 'No disponible'}</TableCell>
                    <TableCell>{miembro?.genero || 'No disponible'}</TableCell>
                    <TableCell>{miembro?.federacion_deporte || 'No disponible'}</TableCell>
                    <TableCell>{miembro?.movilidad_reducida || 'No disponible'}</TableCell>
                    <TableCell>{miembro?.rol || 'No disponible'}</TableCell>
                    <TableCell>{miembro?.tipoalojado || 'No disponible'}</TableCell>
                    <TableCell>{miembro?.es_responsable === 1 ? 'Si' : 'No'}</TableCell>
                    <TableCell>{miembro?.calle || 'No disponible'}</TableCell>
                    <TableCell>{miembro?.numero || 'No disponible'}</TableCell>
                    <TableCell>{miembro?.cp || 'No disponible'}</TableCell>
                    <TableCell>{miembro?.provincia || 'No disponible'}</TableCell>
                    <TableCell>{miembro?.pais || 'No disponible'}</TableCell>
                    <TableCell>
                      <Select
                        value={asignaciones[miembro?.id] || miembro?.habitacion_asignada || ''}
                        onChange={(e) => handleAsignarHabitacion(miembro?.id, e.target.value)}
                        displayEmpty
                        fullWidth
                      >
                        <MenuItem value="" disabled>Seleccionar</MenuItem>
                        {habitaciones.map((habitacion) => (
                          <MenuItem key={habitacion.id} value={habitacion.id}>
                            {habitacion.descripcion}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Button variant="contained" onClick={onClose} sx={{ mt: 3 }}>
          Cerrar
        </Button>
      </Box>
    </Modal>
  );
};  

export default DetalleReservaModal;